// Settings -> Platform Administrators (documentation/platform-administrators.md): the people with
// the platform_admin assignment. They sign in with any method that provides MFA; there is no
// password field here. Changes require a platform administrator's strong (MFA) session.
import { useEffect, useState } from 'react';
import {
  cancelPlatformInvitation,
  disablePlatformAdmin,
  enablePlatformAdmin,
  invitePlatformAdmin,
  listPlatformAdmins,
  removePlatformAdmin,
  finalizeBootstrapAdmin,
  type IssuedInvitation,
  type PlatformAdminOverview,
  type PlatformAdministrator,
  type PlatformInvitation,
} from '../../services/platformAdmins';
import { ApiError } from '../../services/tenancy';
import { Dialog } from './OEMPartnersManager';
import { INVITE_EXPLANATION, STATUS_LABEL, describeFinalize, describeRevoke, rowActions } from './platformAdminModel';
import { METHOD_LABELS } from './peopleModel';

const btn = 'px-3 py-1.5 rounded text-sm font-medium disabled:opacity-40';
const input = 'bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm w-full';

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'The request could not be completed. Please try again.';
}

function when(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function AdministratorsTable({ overview, busy, onDisable, onEnable, onRemove, onFinalize }: {
  overview: PlatformAdminOverview; busy?: boolean;
  onDisable: (a: PlatformAdministrator) => void; onEnable: (a: PlatformAdministrator) => void; onRemove: (a: PlatformAdministrator) => void;
  onFinalize?: (a: PlatformAdministrator) => void;
}) {
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-gray-400">
        <tr>
          <th scope="col" className="py-1">Name</th><th scope="col" className="py-1">Sign-in</th><th scope="col" className="py-1">Status</th>
          <th scope="col" className="py-1">Last sign-in</th><th scope="col" className="py-1">Authorized</th><th scope="col" className="py-1">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-700">
        {overview.administrators.map((a) => {
          const act = rowActions(a, overview);
          return (
            <tr key={a.id}>
              <td className="py-1.5">{a.display_name || a.username}{a.self && <span className="ml-2 text-xs text-blue-300">(you)</span>}</td>
              <td className="py-1.5 break-all text-gray-300">
                {(a.methods || []).map((m) => METHOD_LABELS[m as 'radioos'] || m).join(', ') || '—'}
                {a.microsoft_account && <div className="text-xs text-gray-400">{a.microsoft_account}</div>}
              </td>
              <td className="py-1.5">{STATUS_LABEL[a.status]}</td>
              <td className="py-1.5 text-gray-300">{when(a.last_login_at)}</td>
              <td className="py-1.5 text-gray-300">{when(a.created_at)}{a.authorized_by ? ` by ${a.authorized_by}` : a.legacy_account ? ' (installation)' : ''}</td>
              <td className="py-1.5">
                {act.finalize && onFinalize && (
                  <button className={`${btn} bg-amber-700 hover:bg-amber-600 mb-1`} disabled={busy} onClick={() => onFinalize(a)}>Finalize bootstrap…</button>
                )}
                {act.reason ? <span className="block text-xs text-gray-400">{act.reason}</span> : (
                  <div className="flex flex-wrap gap-2">
                    {act.disable && <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={busy} onClick={() => onDisable(a)}>Disable</button>}
                    {act.enable && <button className={`${btn} bg-green-700 hover:bg-green-600`} disabled={busy} onClick={() => onEnable(a)}>Enable</button>}
                    {act.remove && <button className={`${btn} bg-red-900 hover:bg-red-800`} disabled={busy} onClick={() => onRemove(a)}>Remove</button>}
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function InvitationsTable({ invitations, busy, onCancel }: { invitations: PlatformInvitation[]; busy?: boolean; onCancel: (i: PlatformInvitation) => void }) {
  if (invitations.length === 0) return <p className="text-sm text-gray-400">No pending invitations.</p>;
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-gray-400">
        <tr><th scope="col" className="py-1">E-mail</th><th scope="col" className="py-1">Invited by</th><th scope="col" className="py-1">Created</th>
          <th scope="col" className="py-1">Expires</th><th scope="col" className="py-1">Actions</th></tr>
      </thead>
      <tbody className="divide-y divide-gray-700">
        {invitations.map((i) => (
          <tr key={i.id}>
            <td className="py-1.5 break-all">{i.login_name}</td>
            <td className="py-1.5 text-gray-300">{i.invited_by || '—'}</td>
            <td className="py-1.5 text-gray-300">{when(i.created_at)}</td>
            <td className="py-1.5 text-gray-300">{i.expired ? 'Expired' : when(i.expires_at)}{i.delivery_state === 'sent' ? ' · e-mailed' : ''}</td>
            <td className="py-1.5"><button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={busy} onClick={() => onCancel(i)}>Cancel invitation</button></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function InvitationLinkDialog({ issued, onClose }: { issued: IssuedInvitation; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const url = issued.claim_url;
  return (
    <Dialog title={`Invitation for ${issued.invitation.login_name}`} onCancel={onClose}>
      <p className="text-gray-300">{issued.notice}</p>
      {url && (
        <>
          <p className="font-semibold text-yellow-200">This invitation link is shown once. It cannot be retrieved again.</p>
          <p className="text-gray-300">Send it to <span className="font-mono">{issued.invitation.login_name}</span>. Valid until {when(issued.invitation.expires_at)}.</p>
          <label htmlFor="platform-invitation-link" className="block text-xs text-gray-300">Invitation link</label>
          <input id="platform-invitation-link" readOnly value={url} className={`${input} font-mono`} onFocus={(e) => e.currentTarget.select()} />
          <button className={`${btn} bg-blue-600 hover:bg-blue-500`} onClick={() => { navigator.clipboard?.writeText(url).then(() => setCopied(true)).catch(() => undefined); }}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </>
      )}
    </Dialog>
  );
}

export default function PlatformAdministrators() {
  const [overview, setOverview] = useState<PlatformAdminOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviting, setInviting] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedInvitation | null>(null);
  const [revoking, setRevoking] = useState<{ admin: PlatformAdministrator; kind: 'disable' | 'remove' } | null>(null);
  const [cancelling, setCancelling] = useState<PlatformInvitation | null>(null);
  const [finalizing, setFinalizing] = useState<PlatformAdministrator | null>(null);

  const load = async () => {
    try {
      setOverview(await listPlatformAdmins());
    } catch (err) {
      setError(errorText(err));
    }
  };
  useEffect(() => { load(); }, []);

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setNotice(done);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[16rem]">
          <h2 className="text-xl font-bold">Platform Administrators</h2>
          <p className="text-sm text-gray-400">
            Platform administrators operate the whole RadioOS platform, including its security. They sign in with any method that provides
            multi-factor authentication (Microsoft, Google or a RadioOS password with an authenticator app). Everyone else is managed on the
            People page.
          </p>
        </div>
        <button className={`${btn} bg-blue-600 hover:bg-blue-500`} onClick={() => setInviting('')}>Add Platform Administrator</button>
      </div>
      {error && <div role="alert" className="p-3 rounded bg-red-900/50 border border-red-700 text-red-200 text-sm flex justify-between"><span>{error}</span><button onClick={() => setError(null)} aria-label="Dismiss">×</button></div>}
      {notice && <div role="status" className="p-3 rounded bg-green-900/40 border border-green-700 text-green-200 text-sm flex justify-between"><span>{notice}</span><button onClick={() => setNotice(null)} aria-label="Dismiss">×</button></div>}
      <section className="bg-gray-800 border border-gray-700 rounded p-4 space-y-2">
        <h3 className="font-semibold">Administrators</h3>
        {overview === null ? <p role="status" className="text-sm text-gray-400">Loading…</p> : (
          <AdministratorsTable overview={overview} busy={busy}
            onDisable={(a) => setRevoking({ admin: a, kind: 'disable' })}
            onEnable={(a) => act(() => enablePlatformAdmin(a.id), `${a.display_name || a.username} enabled.`)}
            onRemove={(a) => setRevoking({ admin: a, kind: 'remove' })} onFinalize={setFinalizing} />
        )}
      </section>
      <section className="bg-gray-800 border border-gray-700 rounded p-4 space-y-2">
        <h3 className="font-semibold">Pending invitations</h3>
        {overview && <InvitationsTable invitations={overview.invitations} busy={busy} onCancel={setCancelling} />}
      </section>

      {inviting !== null && (
        <Dialog title="Add Platform Administrator" onCancel={() => setInviting(null)} confirm="Create invitation" busy={busy}
          onConfirm={async () => {
            setBusy(true);
            setError(null);
            try {
              setIssued(await invitePlatformAdmin(inviting));
              setInviting(null);
              await load();
            } catch (err) {
              setError(errorText(err));
            } finally {
              setBusy(false);
            }
          }}>
          <ul className="list-disc pl-5 space-y-1 text-gray-300">{INVITE_EXPLANATION.map((p) => <li key={p}>{p}</li>)}</ul>
          <label htmlFor="platform-invite-login" className="block text-xs text-gray-300">E-mail address</label>
          <input id="platform-invite-login" type="email" className={input} value={inviting} autoComplete="off" onChange={(e) => setInviting(e.target.value)} />
        </Dialog>
      )}
      {issued && <InvitationLinkDialog issued={issued} onClose={() => setIssued(null)} />}
      {revoking && (() => {
        const d = describeRevoke(revoking.admin, revoking.kind);
        return (
          <Dialog title={d.title} onCancel={() => setRevoking(null)} confirm={d.confirm} busy={busy} danger
            onConfirm={async () => {
              const a = revoking.admin;
              await act(() => (revoking.kind === 'disable' ? disablePlatformAdmin(a.id) : removePlatformAdmin(a.id)),
                `${a.display_name || a.username} ${revoking.kind === 'disable' ? 'disabled' : 'removed'}.`);
              setRevoking(null);
            }}>
            <ul className="list-disc pl-5 space-y-1">{d.points.map((p) => <li key={p}>{p}</li>)}</ul>
          </Dialog>
        );
      })()}
      {finalizing && (() => {
        const d = describeFinalize(finalizing);
        return (
          <Dialog title={d.title} onCancel={() => setFinalizing(null)} confirm={d.confirm} busy={busy}
            onConfirm={async () => {
              const a = finalizing;
              await act(() => finalizeBootstrapAdmin(a.id), `${a.display_name || a.username} is now a pure platform account.`);
              setFinalizing(null);
            }}>
            <ul className="list-disc pl-5 space-y-1">{d.points.map((p) => <li key={p}>{p}</li>)}</ul>
          </Dialog>
        );
      })()}
      {cancelling && (
        <Dialog title={`Cancel the invitation for ${cancelling.login_name}?`} onCancel={() => setCancelling(null)} confirm="Cancel invitation" busy={busy} danger
          onConfirm={async () => { await act(() => cancelPlatformInvitation(cancelling.id), 'Invitation cancelled.'); setCancelling(null); }}>
          <p>The invitation link stops working. No access was ever granted by it.</p>
        </Dialog>
      )}
    </div>
  );
}
